import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Error as CriticalIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as OkIcon,
  ChevronLeft as ChevronIcon,
} from '@mui/icons-material';
import { AlertsResponse, AlertSeverity } from '../../services/dashboardService';

const SEVERITY_META: Record<AlertSeverity, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  critical: { label: 'بحرانی', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', icon: <CriticalIcon style={{ width: 22, height: 22, color: '#ef4444' }} /> },
  warning: { label: 'هشدار', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <WarningIcon style={{ width: 22, height: 22, color: '#f59e0b' }} /> },
  info: { label: 'اطلاع', color: '#0ea5e9', bg: 'rgba(14,165,233,0.08)', icon: <InfoIcon style={{ width: 22, height: 22, color: '#0ea5e9' }} /> },
};

const faNum = (n: number) => (n ?? 0).toLocaleString('fa-IR');

interface Props {
  data: AlertsResponse | null;
  loading: boolean;
}

const AlertCenter: React.FC<Props> = ({ data, loading }) => {
  const navigate = useNavigate();

  return (
    <div className="admin-card">
      <div className="admin-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WarningIcon style={{ width: 20, height: 20, color: '#f59e0b' }} />
          <h2 style={{ fontSize: '1rem' }}>مرکز هشدار و ریسک</h2>
        </div>
        {data && (
          <div style={{ display: 'flex', gap: 6 }}>
            {(['critical', 'warning', 'info'] as AlertSeverity[]).map((s) =>
              data.counts[s] > 0 ? (
                <span key={s} className="dash-sev-chip" style={{ color: SEVERITY_META[s].color, backgroundColor: SEVERITY_META[s].bg }}>
                  {SEVERITY_META[s].label}: {faNum(data.counts[s])}
                </span>
              ) : null
            )}
          </div>
        )}
      </div>
      <div className="admin-card-body" style={{ padding: 0 }}>
        {loading && !data ? (
          <div style={{ padding: 20 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton-line w-75" style={{ marginBottom: 12 }} />
            ))}
          </div>
        ) : !data || data.alerts.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 0' }}>
            <OkIcon style={{ color: '#22c55e' }} />
            <p>هیچ هشدار فعالی وجود ندارد — همه‌چیز سالم است ✅</p>
          </div>
        ) : (
          <div>
            {data.alerts.map((a) => {
              const meta = SEVERITY_META[a.severity];
              const clickable = !!a.actionHref;
              return (
                <div
                  key={a.id}
                  className={`dash-alert${clickable ? ' dash-clickable' : ''}`}
                  style={{ borderRight: `3px solid ${meta.color}`, backgroundColor: meta.bg }}
                  onClick={clickable ? () => navigate(a.actionHref!) : undefined}
                >
                  <div className="dash-alert-icon">{meta.icon}</div>
                  <div className="dash-alert-body">
                    <div className="dash-alert-title">
                      {a.title}
                      {typeof a.count === 'number' && <span className="dash-badge" style={{ backgroundColor: meta.color }}>{faNum(a.count)}</span>}
                    </div>
                    <div className="dash-alert-desc">{a.description}</div>
                  </div>
                  {clickable && <ChevronIcon style={{ width: 20, height: 20, color: 'var(--color-text-secondary)' }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertCenter;
